import { CardPermission,type PortfolioWallet } from '@algoboo/schema/api'
import { Button,Card,CreditMeter,EmptyState,Icon,Pill,Skeleton,Tabs,TokenAmount,UiLink,cx,paths,shortAddress } from '@algoboo/ui'
import { createFileRoute,useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { PageHeader } from '../components/Shell'
import { HoldingCard,RewardTotals } from '../components/holdings'
import { DISTRIBUTOR } from '../components/marketing/pricing'
import { SignInPrompt,useSession } from '../components/session'
import { PERMISSION_STATUS,WALLET_STATUS } from '../components/status'
import { AuthorizeDialog } from '../components/wallet/AuthorizeDialog'
import { CreateWalletDialog } from '../components/wallet/CreateWalletDialog'
import { AgentAvatar,NoSuchView,QueryError } from '../components/widgets'
import { creditsQuery, portfolioQuery, request, useCredits, useMyHoldings, usePortfolio } from '../lib/api'
import { usePageMeta } from '../lib/meta'
import { viewOf } from '../lib/views'
import { prefetchSignedIn } from '../lib/route-data'

/**
 * `?card=`: a creation card's suggested permission (`CardPermission`, from /create's "Authorize it with your caps"), as
 * the router's JSON search value or a JSON string; dropped when it does not parse.
 */
const cardParam = z
  .unknown()
  .optional()
  .transform((v): CardPermission | undefined => {
    let raw = v
    if (typeof v === 'string') {
      try {
        raw = JSON.parse(v)
      } catch {
        return undefined
      }
    }
    const p = CardPermission.safeParse(raw)
    return p.success ? p.data : undefined
  })

/** The portfolio's views (lane BOOCLEAN1): the wallet and its agents, and the agent tokens held (was /holdings). */
const TABS = ['wallet', 'tokens'] as const
type Tab = (typeof TABS)[number]

export const Route = createFileRoute('/portfolio/')({
  validateSearch: z.object({ grant: z.string().optional(), card: cardParam, shadow: z.string().optional().catch(undefined), tab: z.string().max(64).optional().catch(undefined) }),
  loader: ({ context: { queryClient: qc } }) => prefetchSignedIn(qc, () => [qc.ensureQueryData(portfolioQuery()), qc.ensureQueryData(creditsQuery())]),
  component: PortfolioPage,
})

function PortfolioPage() {
  usePageMeta('Portfolio')
  const { me, loading } = useSession()
  const { grant, card, shadow, tab } = Route.useSearch()
  const named = viewOf<Tab>(tab, TABS, 'wallet')
  if (loading) return <Skeleton className="h-64 rounded-xl" />
  if (!me) return <SignInPrompt title="Your portfolio" body="Your trading wallet, the agents you authorized, the agent tokens you hold, compute credits live here. Sign in with your wallet to see them." />
  if (named.unknown !== null) return <NoSuchView name={named.unknown} page="The portfolio" />
  return <Portfolio tab={named.view ?? 'wallet'} grant={grant} prefill={card} shadow={shadow} />
}

function Portfolio({ tab, grant, prefill, shadow }: { tab: Tab; grant: string | undefined; prefill: CardPermission | undefined; shadow?: string | undefined }) {
  const portfolio = usePortfolio()
  const credits = useCredits()
  const [creating, setCreating] = useState(false)
  const [authorizing, setAuthorizing] = useState(!!grant)
  // One trading wallet per account (decision 1, lane BOOCLEAN1).
  const pw = portfolio.data?.wallet ?? null
  const ready = pw && pw.wallet.status === 'ready' ? pw : null
  const canRequest = !portfolio.isLoading && !portfolio.error && (!pw || pw.wallet.status === 'refused')
  const navigate = useNavigate()
  /** A shadow's "Follow for real": the permission just granted is named on the shadow, which ends upgraded. */
  const upgradeShadow = async (ref: string, permissionRef: string) => {
    await request('upgradeMyShadow', { params: { ref }, body: { permissionRef } })
    void navigate({ to: '/portfolio/shadows', search: { ref } })
  }

  return (
    <>
      <PageHeader
        eyebrow="Customer"
        title="Portfolio"
        lede="The trading service holds your trading wallet and its keys for you: one wallet per account. Agents only trade inside the permissions you sign with your own wallet, and you can pause, narrow or revoke any of them at any time."
        actions={
          <span className="flex flex-wrap gap-2">
            {ready && (
              <Button icon="shield" onClick={() => setAuthorizing(true)} data-testid="open-authorize">
                Authorize agent
              </Button>
            )}
            {canRequest && (
              <Button variant="primary" icon="plus" onClick={() => setCreating(true)} data-testid="open-create-wallet">
                Trading wallet
              </Button>
            )}
          </span>
        }
      />
      {grant && (
        <div className="mb-5 flex flex-wrap items-center gap-3 rounded-lg border border-line-strong bg-surface-2 px-4 py-3 text-[14px]">
          <span>Choose an agent and sign the limits for your trading wallet.</span>
          {!ready && <span className="text-ink-3">Create your trading wallet first.</span>}
        </div>
      )}
      <div className="mb-5">
        <Tabs
          idBase="portfolio"
          label="Portfolio sections"
          value={tab}
          onChange={(t) => void navigate({ to: '/portfolio', search: (prev) => ({ ...prev, tab: t === 'wallet' ? undefined : t }), replace: true })}
          tabs={[
            { value: 'wallet', label: 'Wallet and agents' },
            { value: 'tokens', label: 'Tokens' },
          ]}
        />
      </div>
      {tab === 'tokens' ? (
        <section id="portfolio-panel" role="tabpanel" aria-labelledby="portfolio-tab-tokens" data-testid="portfolio-tokens">
          <TokensTab />
        </section>
      ) : (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-6">
          <Card className="p-5" data-testid="wallets">
            <h2 className="font-display text-[18px] font-semibold">Your trading wallet</h2>
            {portfolio.isLoading ? (
              <Skeleton className="mt-4 h-40" />
            ) : portfolio.error ? (
              <QueryError error={portfolio.error} refetch={() => void portfolio.refetch()} />
            ) : !pw ? (
              <EmptyState
                compact
                title="No trading wallet yet"
                body="Create your wallet, deposit on Robinhood Chain, then authorize an agent you choose. Nothing trades until you sign."
                action={
                  <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
                    Create your trading wallet
                  </Button>
                }
              />
            ) : (
              <>
                {portfolio.data && !portfolio.data.available && (
                  <p className="rh-state mt-3 flex items-center gap-2 rounded-lg px-3 py-2 text-[13px] text-ink-2" role="status" data-testid="portfolio-degraded">
                    <Icon name="offline" size={14} className="text-warn" /> The trading service is not answering. Balances and positions are unknown right now, not zero.
                  </p>
                )}
                <div className="mt-4 max-w-md">
                  <WalletCard w={pw} available={portfolio.data?.available ?? false} />
                </div>
              </>
            )}
          </Card>

        </div>

        <aside className="space-y-4">
          <Card className="p-5">
            {credits.data ? <CreditMeter availableMicro={credits.data.availableMicro} heldMicro={credits.data.heldMicro} settledMicro={credits.data.settledMicro} /> : <Skeleton className="h-16" />}
            <p className="mt-3 text-[12.5px] text-ink-3">Credits pay for your agents&apos; model calls. Your trading wallet never funds compute.</p>
            <UiLink href="/portfolio/billing" className="mt-3 inline-block text-[13px] font-medium text-accent hover:underline">
              Top up with USDG
            </UiLink>
          </Card>
        </aside>
      </div>
      )}
      <CreateWalletDialog open={creating} onClose={() => setCreating(false)} />
      {authorizing && ready && (
        <AuthorizeDialog
          open
          onClose={() => setAuthorizing(false)}
          wallet={ready.wallet}
          defaultAgentId={grant}
          prefill={grant ? prefill : undefined}
          // From a shadow's "Follow for real" (lane ALGOBOO2): the granted permission is named on the shadow.
          onGranted={shadow ? (permissionRef) => upgradeShadow(shadow, permissionRef) : undefined}
        />
      )}
    </>
  )
}

function WalletCard({ w, available }: { w: PortfolioWallet; available: boolean }) {
  const s = WALLET_STATUS[w.wallet.status]
  const active = w.permissions.filter((p) => p.status === 'active' || p.status === 'paused')
  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="font-display text-[17px] font-semibold">Trading wallet</span>
        <Pill tone={s.tone}>{s.label}</Pill>
      </div>
      <p className="mt-1 font-mono text-[12px] text-ink-3">{w.wallet.address ? shortAddress(w.wallet.address) : 'address pending'}</p>
      <ul className="mt-3 space-y-0.5 text-[12.5px] text-ink-2">
        {!available ? (
          <li className="text-ink-3">Balances unknown</li>
        ) : w.balances.length === 0 ? (
          <li className="text-ink-3">No balances yet</li>
        ) : (
          w.balances.slice(0, 3).map((b) => (
            <li key={b.token.address} className="flex justify-between">
              {b.amount === null ? <span className="text-ink-3">{b.token.symbol ?? 'token'} unknown</span> : <TokenAmount amount={b.amount} token={b.token.address} decimals={b.token.decimals} symbol={b.token.symbol} />}
            </li>
          ))
        )}
      </ul>
      <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-line pt-3">
        {active.length === 0 ? (
          <span className="text-[12px] text-ink-3">No agent authorized</span>
        ) : (
          active.map((p) => (
            <span key={p.permissionRef} className="inline-flex items-center gap-1.5 text-[12px] text-ink-2">
              {p.agent && <AgentAvatar handle={p.agent.handle} name={p.agent.name} avatarUrl={p.agent.avatarUrl} size={18} />}
              {p.agent?.name ?? 'agent'}
              <span className={cx('h-1.5 w-1.5 rounded-full', PERMISSION_STATUS[p.status].tone === 'good' ? 'bg-gain' : 'bg-ink-4')} aria-label={p.status} />
            </span>
          ))
        )}
      </div>
    </>
  )
  const cls = 'block rounded-lg border border-line bg-surface-2 p-4 transition-colors hover:border-line-strong'
  return w.wallet.address ? (
    <UiLink href={paths.wallet()} className={cls} data-testid="wallet-card" data-status={w.wallet.status}>
      {body}
    </UiLink>
  ) : (
    <div className={cls} data-testid="wallet-card" data-status={w.wallet.status}>
      {body}
    </div>
  )
}

/** The Tokens tab (was /holdings, lane BOOCLEAN1): the agent tokens the linked wallets hold and their claims. */
function TokensTab() {
  const q = useMyHoldings()
  const list = q.data ?? []
  return (
    <>
      <p className="mb-4 max-w-3xl text-[13px] text-ink-2">
        Agent tokens held by your linked wallets. Any historical holder rewards already recorded remain claimable; a claim goes straight to the holding wallet. A closed week stays
        claimable for {DISTRIBUTOR.claimWindowEpochs} weeks (about a year), then what is unclaimed rolls forward to the token&apos;s holders at that time.{' '}
        <UiLink href="/me" className="font-medium text-accent hover:underline">
          Linked wallets
        </UiLink>
      </p>
      {q.isLoading ? (
        <Skeleton className="h-48 rounded-xl" />
      ) : q.error ? (
        <QueryError error={q.error} refetch={() => void q.refetch()} compact={false} />
      ) : list.length === 0 ? (
        <EmptyState
          title="No agent tokens yet"
          body="Buy an agent's token from its launch pool and your share of its fees shows up here."
          action={
            <UiLink href="/tokens" className="text-[13px] font-medium text-accent hover:underline">
              Browse tokens
            </UiLink>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-4" data-testid="holdings">
            {list.map((h) => (
              <HoldingCard key={h.token.agent.id} h={h} />
            ))}
          </div>
          <aside className="space-y-4 lg:order-last">
            <RewardTotals holdings={list} />
          </aside>
        </div>
      )}
    </>
  )
}
